import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faMinus,
  faWaveSquare,
  faQuestionCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { DateTime } from "luxon";
import { useState } from "react";
import AvatarCircle from "app/components/AvatarCircle";
import { UserInfo } from "app/containers/Global/types";
import {
  TeamCheckInUserItem,
  TrendCategory,
} from "app/containers/TeamsTab/types";
import {
  trendCategoryLabel,
  trendCategorySemanticColor,
} from "app/containers/TeamsTab/helpers";
import { SmallCustomIcon } from "../../layer00";

export type TeamTableRowProps = {
  item: TeamCheckInUserItem;
  userInfo?: UserInfo;
  onClick: (userAccountId: number, displayName: string) => void;
  onViewSummary: (userAccountId: number, date?: string | null) => void;
  hasDateRange?: boolean;
  showNextOneOnOne?: boolean;
  nextOneOnOneDate?: DateTime | null;
};

const trendIcon: Record<TrendCategory, typeof faArrowTrendUp> = {
  "trending-up": faArrowTrendUp,
  "trending-down": faArrowTrendDown,
  stable: faMinus,
  oscillating: faWaveSquare,
  "no-clear-pattern": faQuestionCircle,
  "not-enough-data": faMinus,
};

export const TeamTableRow = ({
  item,
  userInfo,
  onClick,
  onViewSummary,
  hasDateRange = false,
  showNextOneOnOne = false,
  nextOneOnOneDate,
}: TeamTableRowProps) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const trendColor = (trend: TrendCategory) => {
    switch (trendCategorySemanticColor[trend]) {
      case "success":
        return theme.palette.success.main;
      case "error":
        return theme.palette.error.main;
      case "purple":
        return theme.palette.purple.main;
      default:
        return theme.palette.secondary.main;
    }
  };

  const firstName = userInfo?.firstName ?? "";
  const lastName = userInfo?.lastName ?? "";
  const displayName =
    firstName || lastName
      ? `${firstName} ${lastName}`.trim()
      : `User #${item.userAccountId}`;

  const squareChipSx = {
    width: "1.5rem",
    height: "1.5rem",
    minWidth: "1.5rem",
    padding: 0,
    justifyContent: "center",
    alignItems: "center",
    "& .MuiChip-label": { padding: 0, lineHeight: 0 },
  };

  const completedDate = item.lastCycleCompletedDate
    ? DateTime.fromISO(item.lastCycleCompletedDate)
    : null;

  const getCheckInStatusDisplay = () => {
    if (item.scheduledCount === 0) {
      return (
        <Typography variant="tableCellRegular" color="text.secondary">
          No check-ins scheduled
        </Typography>
      );
    }

    if (hasDateRange && item.scheduledCount > 1) {
      return (
        <Stack direction="row" alignItems="center" columnGap=".375rem">
          {item.completedCount === item.scheduledCount ? (
            <Chip
              label={
                <SmallCustomIcon
                  icon="CHECK"
                  stroke={theme.palette.success.main}
                />
              }
              sx={squareChipSx}
              variant="success"
            />
          ) : (
            <Chip
              label={
                <SmallCustomIcon
                  icon="SLASH"
                  stroke={theme.palette.secondary.main}
                />
              }
              sx={squareChipSx}
              className="bg-teamApp-chip-inactive-bg"
            />
          )}
          <Typography variant="tableCellRegular" color="text.secondary">
            {item.completedCount}/{item.scheduledCount}
          </Typography>
        </Stack>
      );
    }
    if (item.completedCount === item.scheduledCount) {
      return (
        <Chip
          label={
            <SmallCustomIcon icon="CHECK" stroke={theme.palette.success.main} />
          }
          sx={squareChipSx}
          variant="success"
        />
      );
    }

    return (
      <Chip
        label={
          <SmallCustomIcon icon="SLASH" stroke={theme.palette.secondary.main} />
        }
        sx={squareChipSx}
        className="bg-teamApp-chip-inactive-bg"
      />
    );
  };

  const getOneOnOneString = (date: DateTime) => {
    const diff = date.diffNow(["days", "hours", "minutes"]);
    const totalMinutes = date.diffNow("minutes").minutes;
    const totalHours = date.diffNow("hours").hours;

    if (totalMinutes <= 0) return "Now";
    if (totalHours < 1) return `In ${Math.round(totalMinutes)} Minutes`;
    if (totalHours < 24) return `In ${Math.round(totalHours)} Hours`;
    return `In ${Math.round(diff.days)} Days`;
  };

  return (
    <TableRow
      sx={{
        cursor: "pointer",
        "&:hover": { backgroundColor: (t) => t.palette.action.hover },
      }}
      onClick={() => {
        setMenuAnchor(null);
        onViewSummary(item.userAccountId, item.lastCycleScheduledDate);
      }}
    >
      <TableCell>
        <Box display="flex" flexDirection="row" alignItems="center">
          <AvatarCircle
            userAccountId={item.userAccountId}
            size="small"
            className="mr-3"
          />
          <Stack direction="column">
            <Typography variant="tableCellBold">{displayName}</Typography>
            {userInfo?.jobTitle && (
              <Typography variant="tableCellRegular" color="text.secondary">
                {userInfo.jobTitle}
              </Typography>
            )}
          </Stack>
        </Box>
      </TableCell>

      <TableCell>
        <Stack direction="row" alignItems="center" columnGap=".5rem">
          {item.trendDirection === null ? (
            <Typography variant="tableCellRegular" color="text.secondary">
              —
            </Typography>
          ) : (
            <>
              <Chip
                label={
                  <FontAwesomeIcon
                    icon={trendIcon[item.trendDirection]}
                    color={trendColor(item.trendDirection)}
                  />
                }
                sx={{
                  ...squareChipSx,
                  ...(item.trendDirection === "oscillating" && {
                    backgroundColor: alpha(theme.palette.purple.main, 0.12),
                  }),
                }}
                className={
                  item.trendDirection === "oscillating"
                    ? undefined
                    : "bg-teamApp-chip-inactive-bg"
                }
              />
              <Typography variant="tableCellRegular">
                {trendCategoryLabel[item.trendDirection]}
              </Typography>
            </>
          )}
        </Stack>
      </TableCell>

      <TableCell>
        {completedDate ? (
          <Box display="flex" flexDirection="column">
            <Typography variant="tableCellBold">
              {completedDate.toFormat("MMM d")}
            </Typography>
            <Typography variant="tableCellRegular">
              {completedDate.toFormat("h:mm a")}
            </Typography>
          </Box>
        ) : (
          <Typography variant="tableCellRegular" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>

      {showNextOneOnOne && (
        <TableCell>
          {nextOneOnOneDate ? (
            <Box display="flex" flexDirection="column">
              <Typography variant="tableCellBold">
                {getOneOnOneString(nextOneOnOneDate)}
              </Typography>
              <Typography variant="tableCellRegular">
                {nextOneOnOneDate.toFormat("t")}
              </Typography>
            </Box>
          ) : (
            <Typography variant="tableCellRegular" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
      )}

      <TableCell>
        <Box display="flex" flexDirection="column">
          <Typography variant="tableCellRegular">
            {item.lastCycleSummaryText ?? "-"}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>{getCheckInStatusDisplay()}</TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <IconButton
          size="small"
          sx={{ border: "none" }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="More actions"
        >
          <SmallCustomIcon icon="MOREVERTICAL" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              onClick(item.userAccountId, displayName);
            }}
          >
            View User's Team Members or Direct Reports
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
